import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Formik, FormikProps, useField, useFormikContext } from 'formik';
import { Alert, Badge, Button, FormControl, FormGroup, FormLabel, Stack, Tab, Tabs } from 'react-bootstrap';
import {
  CalendarView,
  CalendarViewEvent,
  fetchApi,
  FormBlockField,
  FormRow,
  makeResetCache,
  selectUser,
  notify,
  notifyError,
  notifyResponse,
  ReferenceMultiSelect,
  useCalendarRange,
  useCalendarRangeQuery,
  UserState,
  validateRow,
} from 'gossamer-front-end';
import { Field, FieldType, ReferenceField } from 'gossamer-unified';
import {
  AdminCreateEventRequest,
  Event,
  EventExtendedView,
  EventExtendedViewRow,
  EventUpload,
  EventUploadRow,
  Ticket,
  UserView,
} from 'cb-model';
import { asUpdateRequest, convertEventToCreateRequest, defaultEventDetails } from '../form/eventFormUtils';
import {
  countActiveFilters,
  emptyFilters,
  getFilterConditions,
  groupsField,
  TimetableFilters,
  TimetableFilterValues,
  useDebouncedValue,
} from './TimetableFilters';
import classes from './TimetableView.module.css';

// host_user_ids is a form-only '|'-separated list of user ids - the first is the main host, the rest co-hosts
type EventRow = FormRow<EventUploadRow & { host_user_ids: string }>;

const hostsField: ReferenceField = {
  seq: 0,
  type: FieldType.Reference,
  label: 'Host(s)',
  mandatory: true,
  refEntity: UserView,
  description: 'the first is the main host, any others are co-hosts',
};

const splitIds = (ids: string | null | undefined) => (ids || '').split('|').filter((id) => !!id);

interface TimetableCalendarEvent extends CalendarViewEvent {
  draggable: boolean;
  unsaved: boolean;
}

type FocusArea = 'calendar' | 'sidebar';

type SidebarTab = 'filters' | 'edit';

// calendar slot size in minutes, also the step used when looking for a free slot to paste into
const STEP_MINUTES = 15;
const SLOT_MS = STEP_MINUTES * 60 * 1000;
const DEFAULT_DURATION_MS = 60 * 60 * 1000;
const DEFAULT_START_HOUR = 9;
// how far ahead to look for a free slot before giving up and allowing an overlap
const MAX_FREE_SLOT_SEARCH = (366 * 24 * 60 * 60 * 1000) / SLOT_MS;
// matches the limit applied by useCalendarRangeQuery
const EXISTING_EVENTS_LIMIT = 1000;

// the one event that is not yet saved has this key; saved events have getExistingKey() keys
const NEW_EVENT_KEY = 'new';
const EXISTING_KEY_PREFIX = 'existing-';

// a blank new event, hosted by the given user
const makeBlankEvent = (userId: string | undefined): EventRow => ({
  _action: 'C',
  _temp_new_key: '',
  host_user_ids: userId || undefined,
});

const UNSAVED_CHANGES_MESSAGE = 'Save or clear your unsaved changes first';
const CANNOT_DELETE_MESSAGE = 'Saved events cannot be deleted here';
const MULTI_DAY_MESSAGE = 'Events spanning more than one day cannot be edited here';

// local (not UTC) date and time strings, matching how event_date/start_time/end_time are interpreted
const pad = (n: number) => String(n).padStart(2, '0');
const toLocalDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

const getStart = (event: EventRow) => new Date(event.event_date + 'T' + event.start_time);
const getEnd = (event: EventRow) => new Date(event.event_date + 'T' + event.end_time);
const hasValidTimes = (event: EventRow) => {
  const start = getStart(event).getTime();
  const end = getEnd(event).getTime();
  return !Number.isNaN(start) && !Number.isNaN(end) && end > start;
};
const getDuration = (event: EventRow) =>
  hasValidTimes(event) ? getEnd(event).getTime() - getStart(event).getTime() : DEFAULT_DURATION_MS;

const withTimes = (event: EventRow, start: Date, end: Date): EventRow => ({
  ...event,
  event_date: toLocalDate(start),
  start_time: toLocalTime(start),
  end_time: toLocalTime(end),
});

// end_time is a time on event_date, so an event must end on the day it starts - shorten it to end at the last
// slot boundary of the day (or 23:59 if it starts in the last slot)
const capEndToDay = (start: Date, end: Date): Date => {
  const lastSlotStart = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 24, -STEP_MINUTES);
  const latestEnd =
    start < lastSlotStart ? lastSlotStart : new Date(start.getFullYear(), start.getMonth(), start.getDate(), 23, 59);
  return end > latestEnd ? latestEnd : end;
};

interface Interval {
  start: number;
  end: number;
}

// first start time at or after the given one, stepping by slot, at which an event of the given duration neither
// overlaps a busy interval nor crosses midnight (as end_time is on the same event_date)
const findFreeStart = (start: Date, durationMs: number, busy: Interval[]): Date => {
  let candidate = start.getTime();
  for (let i = 0; i < MAX_FREE_SLOT_SEARCH; i++) {
    const candidateEnd = candidate + durationMs;
    const sameDay = toLocalDate(new Date(candidate)) === toLocalDate(new Date(candidateEnd - 1));
    if (sameDay && !busy.some((b) => b.start < candidateEnd && candidate < b.end)) {
      return new Date(candidate);
    }
    candidate += SLOT_MS;
  }
  return start;
};

const isUnsavedNew = (event: EventRow) => event._action === 'C' && event._temp_new_key === NEW_EVENT_KEY;

const getExistingKey = (id: number | string) => EXISTING_KEY_PREFIX + id;

// the form only holds a start and end time on one date
const isMultiDay = (row: EventExtendedViewRow) =>
  toLocalDate(new Date(row.start_at)) !== toLocalDate(new Date(row.end_at));

// the form values for a saved event, identified by _original_key (its id)
const toEventRow = (row: EventExtendedViewRow): EventRow =>
  withTimes(
    {
      _action: 'U',
      _temp_new_key: getExistingKey(row.id),
      _original_key: String(row.id),
      title: row.title,
      type: row.type,
      event_profile_id: row.event_profile_id,
      location_type: row.location_type,
      location: row.location,
      groups_to_allocate_to: (row.groups || []).join('|') || undefined,
      host_user_ids: [row.main_host_user_id, ...(row.co_hosts || [])].filter((id) => !!id).join('|') || undefined,
    },
    new Date(row.start_at),
    new Date(row.end_at),
  );

// a new, not-yet-saved event with the field values of the given one
const asNewEvent = (event: EventRow): EventRow => ({
  ...event,
  _action: 'C',
  _temp_new_key: NEW_EVENT_KEY,
  _original_key: undefined,
});

// the fields edited here, as event create/update request properties
const toRequestFields = (values: EventRow): Partial<AdminCreateEventRequest> => ({
  title: values.title,
  type: values.type,
  start_date: getStart(values).toISOString(),
  end_date: getEnd(values).toISOString(),
  event_profile_id: values.event_profile_id ? Number(values.event_profile_id) : null,
  location_type: values.location_type,
  location: values.location_type === 'online' ? '' : values.location || '',
  institution_group_ids: splitIds(values.groups_to_allocate_to).map((id) => parseInt(id, 10)),
  main_host_user_id: splitIds(values.host_user_ids)[0],
  co_host_user_ids: splitIds(values.host_user_ids).slice(1),
});

export const TimetableView = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const user = useSelector((state: { user: UserState }) => selectUser(state));
  const blankEvent = React.useMemo(() => makeBlankEvent(user?.userId), [user?.userId]);
  // the selected event - NEW_EVENT_KEY for the one new, unsaved event, or the key of a saved event - whose values,
  // including unsaved changes, are held by the form
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  // the selected saved event, kept so that it stays shown if a filter change means it is no longer loaded
  const [selectedExistingRow, setSelectedExistingRow] = React.useState<EventExtendedViewRow | null>(null);
  // live form values, so that edits are reflected in the calendar before they are saved
  const [draft, setDraft] = React.useState<EventRow>(blankEvent);
  const [focusArea, setFocusArea] = React.useState<FocusArea | null>(null);
  const [sidebarTab, setSidebarTab] = React.useState<SidebarTab>('filters');
  const [filters, setFilters] = React.useState<TimetableFilterValues>(emptyFilters);
  const copyBuffer = React.useRef<EventRow | null>(null);
  const formikRef = React.useRef<FormikProps<EventRow>>(null);
  const calendarRef = React.useRef<HTMLDivElement>(null);
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const { range } = useCalendarRange();
  const debouncedFilters = useDebouncedValue(filters, 400);
  const existingQuery = useCalendarRangeQuery(EventExtendedView, getFilterConditions(debouncedFilters), [
    ['start_at', 'ASC'],
  ]);
  const existingRows =
    selectedExistingRow && !existingQuery.rows.some((row) => row.id === selectedExistingRow.id)
      ? [...existingQuery.rows, selectedExistingRow]
      : existingQuery.rows;

  const findRow = (key: string | null) => (key && existingRows.find((row) => getExistingKey(row.id) === key)) || null;

  // unsaved changes to the selected event, or a new event that has not been saved at all
  const hasUnsavedChanges = () => {
    const form = formikRef.current;
    return !!form && (form.dirty || isUnsavedNew(form.values));
  };

  const checkCanChangeSelection = (): boolean => {
    if (hasUnsavedChanges()) {
      notify([UNSAVED_CHANGES_MESSAGE, 'danger']);
      return false;
    }
    return true;
  };

  const loadIntoForm = (event: EventRow | null, row: EventExtendedViewRow | null = null) => {
    setSelectedKey(event?._temp_new_key || null);
    setSelectedExistingRow(row);
    formikRef.current?.resetForm({ values: event || blankEvent });
  };

  // the selected event as currently shown - including unsaved edits, but keeping the saved date/times while the
  // edited ones are incomplete
  const getSelectedEvent = (): EventRow | null => {
    if (!selectedKey) {
      return null;
    }
    const row = findRow(selectedKey);
    if (draft._temp_new_key !== selectedKey) {
      return row ? toEventRow(row) : null;
    }
    if (hasValidTimes(draft) || !row) {
      return draft;
    }
    return withTimes(draft, new Date(row.start_at), new Date(row.end_at));
  };

  const saveEvent = async (values: EventRow) => {
    if (!hasValidTimes(values)) {
      notify(['Enter a valid date, and start and end times (hh:mm) with the end after the start', 'danger']);
      return;
    }
    const row = values._original_key ? findRow(values._temp_new_key || null) : null;
    if (values._original_key && !row) {
      // e.g. just saved and not yet reloaded - must not fall through to creating a duplicate
      notify(['This event is still loading - please try again in a moment', 'warning']);
      return;
    }
    try {
      let eventId: number | undefined;
      if (row) {
        // send all the event's current values, as the update replaces them all (including tickets)
        const payload = asUpdateRequest(row.id, { ...convertEventToCreateRequest(row), ...toRequestFields(values) });
        if (payload.type === 'on-demand') {
          delete payload.start_date;
          delete payload.end_date;
        }
        notifyResponse(await fetchApi('POST', `/main/event-update`, payload));
        eventId = row.id;
      } else {
        const payload: AdminCreateEventRequest = { ...defaultEventDetails(), ...toRequestFields(values) };
        if (payload.type === 'on-demand') {
          delete payload.start_date;
          delete payload.end_date;
        }
        const response = await fetchApi('POST', `/main/event-create`, payload);
        notifyResponse(response);
        eventId = (response as { eventId?: number }).eventId;
      }
      resetCache(EventExtendedView.id, eventId ? String(eventId) : undefined);
      resetCache(Event.id, eventId ? String(eventId) : undefined);
      resetCache(Ticket.id, eventId ? String(eventId) : undefined);
      // keep the saved event selected - its row is reloaded with the cache reset
      if (eventId) {
        loadIntoForm({
          ...values,
          _action: 'U',
          _temp_new_key: getExistingKey(eventId),
          _original_key: String(eventId),
        });
      } else {
        loadIntoForm(null);
      }
    } catch (e) {
      notifyError(e, 'Could not save event');
    }
  };

  // discard a new event, or unsaved changes to a saved one
  const clearChanges = () => {
    const form = formikRef.current;
    if (form && isUnsavedNew(form.values)) {
      loadIntoForm(null);
    } else {
      form?.resetForm();
    }
  };

  const handleEventSelect = (calendarEvent: TimetableCalendarEvent) => {
    setSidebarTab('edit');
    if (calendarEvent.id === selectedKey || !checkCanChangeSelection()) {
      return;
    }
    const row = findRow(calendarEvent.id);
    if (row) {
      loadIntoForm(toEventRow(row), row);
    }
  };

  // create a new event in the clicked slot, copying all other field values from the selected event if there is
  // one, or else from any values already entered in the form
  const handleSlotSelect = (slot: { start: Date; end: Date; action?: string }) => {
    setSidebarTab('edit');
    if (selectedKey && !checkCanChangeSelection()) {
      return;
    }
    const base = (selectedKey && getSelectedEvent()) || formikRef.current?.values || blankEvent;
    // a copied event keeps its duration unless a range of slots was dragged out
    const durationMs = selectedKey ? getDuration(base) : DEFAULT_DURATION_MS;
    let { start, end } = slot;
    // a day slot in month view - pick a sensible default time
    if (end.getTime() - start.getTime() >= 24 * 60 * 60 * 1000) {
      start = new Date(start.getFullYear(), start.getMonth(), start.getDate(), DEFAULT_START_HOUR);
      end = new Date(start.getTime() + durationMs);
    } else if (slot.action === 'click') {
      // a single click selects one (short) slot - use the duration instead; dragging selects a range
      end = new Date(start.getTime() + durationMs);
    }
    loadIntoForm(withTimes(asNewEvent(base), start, capEndToDay(start, end)));
  };

  // the current values of the event being dragged or resized, selecting it first if necessary - null if it can't be
  // selected because of unsaved changes to another event
  const getValuesForChange = (key: string): EventRow | null => {
    if (key === selectedKey) {
      return formikRef.current?.values || null;
    }
    const row = findRow(key);
    if (!row || !checkCanChangeSelection()) {
      return null;
    }
    const event = toEventRow(row);
    loadIntoForm(event, row);
    return event;
  };

  // a moved or resized event becomes the selected event with unsaved changes - the change is only saved on Save
  const handleEventDrop = (calendarEvent: TimetableCalendarEvent, start: Date) => {
    const values = getValuesForChange(calendarEvent.id);
    if (values) {
      formikRef.current?.setValues(withTimes(values, start, new Date(start.getTime() + getDuration(values))));
    }
  };

  // only the end time can change - the top resize handle is hidden, but ignore any change of start regardless
  const handleEventResize = (calendarEvent: TimetableCalendarEvent, _start: Date, end: Date) => {
    const values = getValuesForChange(calendarEvent.id);
    if (!values || !hasValidTimes(values)) {
      return;
    }
    const start = getStart(values);
    if (end > start && toLocalDate(end) === toLocalDate(start)) {
      formikRef.current?.setValues({ ...values, end_time: toLocalTime(end) });
    }
  };

  const copySelected = (): boolean => {
    const selected = getSelectedEvent();
    if (selected) {
      copyBuffer.current = { ...selected };
    }
    return !!selected;
  };

  // only the new, unsaved event can be deleted (i.e. discarded)
  const deleteSelected = (): boolean => {
    if (!selectedKey) {
      return false;
    }
    if (selectedKey !== NEW_EVENT_KEY) {
      notify([CANNOT_DELETE_MESSAGE, 'warning']);
      return false;
    }
    loadIntoForm(null);
    return true;
  };

  // paste a new event at the copied event's start if visible, otherwise at the same time on the first visible date,
  // moving on to the next free slot if that time is taken
  const paste = () => {
    const source = copyBuffer.current;
    if (!source || !checkCanChangeSelection()) {
      return;
    }
    const durationMs = getDuration(source);
    let start = hasValidTimes(source) ? getStart(source) : null;
    if (!start || start < range.start || start > range.end) {
      const hours = start ? start.getHours() : DEFAULT_START_HOUR;
      const minutes = start ? start.getMinutes() : 0;
      start = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate(), hours, minutes);
    }
    // events count as busy even when filtered out, but only those in the visible range are loaded
    const busy: Interval[] = existingRows.map((row) => ({
      start: new Date(row.start_at).getTime(),
      end: new Date(row.end_at).getTime(),
    }));
    start = findFreeStart(start, durationMs, busy);
    loadIntoForm(withTimes(asNewEvent(source), start, new Date(start.getTime() + durationMs)));
    setSidebarTab('edit');
  };

  const focusSidebar = () => {
    setSidebarTab('edit');
    // the edit tab is the only one with a <form> - wait for it to be shown before focusing
    window.setTimeout(() => {
      sidebarRef.current?.querySelector<HTMLElement>('form input, form select, form textarea')?.focus();
    });
  };

  const handleCalendarKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const key = event.key.toLowerCase();
    const modifier = event.ctrlKey || event.metaKey;
    if (modifier && key === 'c') {
      copySelected();
    } else if (modifier && key === 'x') {
      if (selectedKey === NEW_EVENT_KEY && copySelected()) {
        deleteSelected();
      } else if (selectedKey) {
        notify([CANNOT_DELETE_MESSAGE, 'warning']);
      }
    } else if (modifier && key === 'v') {
      paste();
    } else if (event.key === 'Delete') {
      deleteSelected();
    } else if (event.key === 'Enter' && selectedKey && !(event.target instanceof HTMLButtonElement)) {
      focusSidebar();
    } else {
      return;
    }
    event.preventDefault();
  };

  const focusProps = (area: FocusArea) => ({
    onFocus: () => setFocusArea(area),
    onBlur: (event: React.FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setFocusArea((current) => (current === area ? null : current));
      }
    },
  });

  // the saved events, with the selected one showing its unsaved changes, plus the new event if there is one
  const selectedEvent = getSelectedEvent();
  const selectedUnsaved = hasUnsavedChanges();
  const calendarEvents: TimetableCalendarEvent[] = existingRows.map((row) => {
    const key = getExistingKey(row.id);
    const isSelected = key === selectedKey && !!selectedEvent && hasValidTimes(selectedEvent);
    return {
      id: key,
      title: (isSelected ? selectedEvent.title : row.title) || '(untitled)',
      start: isSelected ? getStart(selectedEvent) : new Date(row.start_at),
      end: isSelected ? getEnd(selectedEvent) : new Date(row.end_at),
      draggable: !isMultiDay(row),
      unsaved: isSelected && selectedUnsaved,
    };
  });
  if (selectedKey === NEW_EVENT_KEY && selectedEvent && hasValidTimes(selectedEvent)) {
    calendarEvents.push({
      id: NEW_EVENT_KEY,
      title: selectedEvent.title || '(untitled)',
      start: getStart(selectedEvent),
      end: getEnd(selectedEvent),
      draggable: true,
      unsaved: true,
    });
  }

  const selectedRow = findRow(selectedKey);

  return (
    <Stack direction="horizontal" gap={3} style={{ alignItems: 'flex-start' }}>
      <div
        ref={calendarRef}
        className={`${classes.Panel} ${classes.Calendar} ${focusArea === 'calendar' ? classes.Focused : ''}`}
        tabIndex={0}
        onKeyDown={handleCalendarKeyDown}
        onMouseDown={(event) => {
          // react-big-calendar's slot/event elements are not focusable, so ensure clicks focus the calendar
          const target = event.target as HTMLElement;
          if (target.closest('button, input, select, textarea, a, [tabindex]') === calendarRef.current) {
            calendarRef.current?.focus({ preventScroll: true });
          }
        }}
        {...focusProps('calendar')}
      >
        {existingQuery.query.error && (
          <Alert variant="danger" className="py-1">
            Failed to load events: {existingQuery.query.error}
          </Alert>
        )}
        {existingQuery.rows.length >= EXISTING_EVENTS_LIMIT && (
          <Alert variant="warning" className="py-1">
            Only the first {EXISTING_EVENTS_LIMIT} events are shown - narrow the filters or the date range
          </Alert>
        )}
        <CalendarView<TimetableCalendarEvent>
          events={calendarEvents}
          selected={calendarEvents.find((e) => e.id === selectedKey)}
          handleEventSelect={handleEventSelect}
          handleSlotSelect={handleSlotSelect}
          handleEventDrop={handleEventDrop}
          handleEventResize={handleEventResize}
          isDraggable={(event) => event.draggable}
          step={STEP_MINUTES}
          timeslots={60 / STEP_MINUTES}
          eventPropGetter={(event) => ({
            className: [
              event.id === selectedKey ? classes.SelectedEvent : '',
              event.unsaved ? classes.UnsavedEvent : '',
            ].join(' '),
            style: {
              color: '#FFFFFF',
            },
          })}
        />
      </div>
      <div
        ref={sidebarRef}
        className={`${classes.Panel} ${classes.Sidebar} ${focusArea === 'sidebar' ? classes.Focused : ''}`}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            calendarRef.current?.focus();
          }
        }}
        {...focusProps('sidebar')}
      >
        <Tabs
          activeKey={sidebarTab}
          onSelect={(key) => setSidebarTab(key === 'edit' ? 'edit' : 'filters')}
          className="mb-3"
        >
          <Tab
            eventKey="filters"
            title={countActiveFilters(filters) ? `Filters (${countActiveFilters(filters)})` : 'Filters'}
          >
            <TimetableFilters filters={filters} setFilters={setFilters} />
          </Tab>
          {/* both tabs stay mounted, so the edit form keeps its state while the filters are shown */}
          <Tab eventKey="edit" title="Edit/Create Event">
            <Sidebar
              blankEvent={blankEvent}
              formikRef={formikRef}
              onClear={clearChanges}
              onDraftChange={setDraft}
              readOnlyReason={selectedRow && isMultiDay(selectedRow) ? MULTI_DAY_MESSAGE : undefined}
              saveEvent={saveEvent}
            />
          </Tab>
        </Tabs>
      </div>
    </Stack>
  );
};

interface FloatingFieldProps {
  editable: boolean;
  fieldId: string;
  field: Field;
}

const FloatingField = (props: FloatingFieldProps): React.JSX.Element => {
  return (
    <div className="mb-2">
      <FormBlockField fullId={props.fieldId} field={props.field} editable={props.editable} />
    </div>
  );
};

interface MultiReferenceFieldProps {
  editable: boolean;
  field: ReferenceField;
  fieldId: 'groups_to_allocate_to' | 'host_user_ids';
  modalTitle: string;
}

// a form value holding a '|'-separated list of ids (e.g. groups_to_allocate_to, as in the bulk upload spreadsheet)
const MultiReferenceField = (props: MultiReferenceFieldProps): React.JSX.Element => {
  const [field, meta, helper] = useField<string | null | undefined>(props.fieldId);
  const { submitCount } = useFormikContext();
  const showValidity = meta.touched || submitCount > 0;
  return (
    <FormGroup className="mb-2" controlId={props.fieldId}>
      <FormLabel>
        {props.field.label}
        {props.field.mandatory && props.editable ? ' *' : ''}
        {props.field.description && (
          <>
            &nbsp;&nbsp;<a title={props.field.description}>ⓘ</a>
          </>
        )}
      </FormLabel>
      <ReferenceMultiSelect
        editable={props.editable}
        field={props.field}
        modalTitle={props.modalTitle}
        onChange={(newValue) => helper.setValue(newValue.length ? newValue.join('|') : null)}
        validity={showValidity && meta.error ? false : undefined}
        value={splitIds(field.value)}
      />
      {meta.error && showValidity && <FormControl.Feedback type="invalid">{meta.error}</FormControl.Feedback>}
    </FormGroup>
  );
};

interface DraftReporterProps {
  onDraftChange: (values: EventRow) => void;
}

const DraftReporter = (props: DraftReporterProps): null => {
  const { values } = useFormikContext<EventRow>();
  React.useEffect(() => {
    props.onDraftChange(values);
  }, [values]);
  return null;
};

interface SidebarProps {
  blankEvent: EventRow;
  formikRef: React.Ref<FormikProps<EventRow>>;
  onClear: () => void;
  onDraftChange: (values: EventRow) => void;
  readOnlyReason?: string;
  saveEvent: (event: EventRow) => Promise<void>;
}

const Sidebar = (props: SidebarProps): React.JSX.Element => {
  return (
    <Formik<EventRow>
      innerRef={props.formikRef}
      initialValues={props.blankEvent}
      onSubmit={(values) => props.saveEvent(values)}
      validate={(values) => {
        const errors: Record<string, string> = { ...validateRow(EventUpload, values) };
        if (splitIds(values.host_user_ids).length === 0) {
          errors.host_user_ids = 'at least one host is required';
        }
        return errors;
      }}
    >
      {(formikProps) => {
        const unsaved = formikProps.dirty || isUnsavedNew(formikProps.values);
        const editable = !props.readOnlyReason;

        return (
          <form onSubmit={formikProps.handleSubmit}>
            <DraftReporter onDraftChange={props.onDraftChange} />
            <Stack direction="horizontal" gap={2}>
              {formikProps.values._action === 'C' ? (
                <Badge bg="primary">New Event</Badge>
              ) : (
                <Badge bg="info">Edit Event</Badge>
              )}
              {unsaved ? <Badge bg="warning">Unsaved</Badge> : <Badge bg="success">Saved</Badge>}
              {formikProps.values._original_key && (
                <Link to={`/admin/event/display/${formikProps.values._original_key}`}>Go to Event</Link>
              )}
            </Stack>
            {props.readOnlyReason && (
              <Alert variant="secondary" className="py-1">
                {props.readOnlyReason}
              </Alert>
            )}
            <FloatingField editable={editable} fieldId="title" field={EventUpload.fields.title} />
            <FloatingField editable={editable} fieldId="type" field={EventUpload.fields.type} />
            <Stack direction="horizontal" gap={2}>
              <FloatingField editable={editable} fieldId="event_date" field={EventUpload.fields.event_date} />
              <FloatingField editable={editable} fieldId="start_time" field={EventUpload.fields.start_time} />
              <FloatingField editable={editable} fieldId="end_time" field={EventUpload.fields.end_time} />
            </Stack>
            <FloatingField editable={editable} fieldId="event_profile_id" field={EventUpload.fields.event_profile_id} />
            <FloatingField editable={editable} fieldId="location_type" field={EventUpload.fields.location_type} />
            {!!formikProps.values.location_type && formikProps.values.location_type !== 'online' && (
              <FloatingField editable={editable} fieldId="location" field={EventUpload.fields.location} />
            )}
            <MultiReferenceField
              editable={editable}
              field={hostsField}
              fieldId="host_user_ids"
              modalTitle="Add a Host"
            />
            <MultiReferenceField
              editable={editable}
              field={groupsField}
              fieldId="groups_to_allocate_to"
              modalTitle="Add a Group"
            />
            {editable && (
              <Stack direction="horizontal" gap={2}>
                <Button size="sm" type="submit" variant="primary" disabled={!unsaved || formikProps.isSubmitting}>
                  {formikProps.isSubmitting ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  type="button"
                  variant="secondary"
                  disabled={!unsaved || formikProps.isSubmitting}
                  onClick={props.onClear}
                >
                  Clear
                </Button>
              </Stack>
            )}
          </form>
        );
      }}
    </Formik>
  );
};
